import type {
  FlowNodeData, FlowNodeMcpInfo, ModelConfig, TimbreItem,
} from '$/domains/workFlowV3/type';
import type { PreviewMediaItem } from '$/pages/RoboneoChatBoard/type';
import type { MaterialModalItem } from '$/pages/RoboneoTeamFlow/Sidebar/MaterialModal';
import {
  clampMenuFullScreenSize,
  MENU_FULL_SCREEN_DEFAULT_SIZE,
  type MenuFullScreenSize,
} from '$/pages/RoboneoTeamFlow/FlowContent/components/Menus/BottomMenuCollapse/menuFullScreenSize';
import type React from 'react';
import {
  StateCreator, UseBoundStore, StoreApi, create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import { TVFlowNodeNextTypeEnum } from '$/pages/RoboneoTeamFlow/config/node.config';
import type { Write, WithImmer } from './immer.d';
import type { StoreSubscribeWithSelector } from './subscribe.d';
import {
  getTaskSpeed,
  setTaskSpeed,
  type TeamFlowQueueMode,
  type TeamFlowTaskSpeed,
} from './useTaskSpeedStore';

export type { TeamFlowQueueMode, TeamFlowTaskSpeed } from './useTaskSpeedStore';

export interface ConcurrentTaskLimitModalState {
  totalCount?: number;
  maxConcurrent?: number;
}

/** 节点萝卜计费缓存：原价 cost + 可选折扣价 discount_cost。 */
export interface NodeCostInfo {
  cost: number;
  discount_cost?: number;
}

interface IFlowNextState {
  inShared: boolean;
  projectId: string;
  roomId: string;
  caseId: string;
  hasNodeOperation: boolean;
  modelConfig: ModelConfig;
  cancelModelNodeId: string;
  canceledPollingNodeId: string;
  timbreConfigNodeId: string | null;
  timbreList: TimbreItem[];
  /** 运镜素材列表缓存，打开运镜弹窗时优先复用。 */
  movingShotItems: MaterialModalItem[];
  movingShotLoaded: boolean;
  movingShotLoading: boolean;
  costNodeMap: Record<string, NodeCostInfo | undefined>;
  /** 画布/Agent 共用的媒体预览入口，由宿主注入 PreviewHeaderMenu 等能力。 */
  onPreviewMedia?: (params: { mediaList: PreviewMediaItem[]; index: number; }) => void;
  previewHeaderMenuSlots: React.ReactElement | null;
  onAddChat: ((data: FlowNodeData, flowNodeType: TVFlowNodeNextTypeEnum) => void) | null;
  isMenuFullScreen: boolean;
  menuFullScreenSize: MenuFullScreenSize;
  commonModelMcpInfoMap: Partial<Record<TVFlowNodeNextTypeEnum, FlowNodeMcpInfo>>;
  /** 兼容旧调用方读取，真实请求速率以 useTaskSpeedStore 为准。 */
  queueMode: TeamFlowTaskSpeed;
  /** 节点执行命中 1001 时展示的并发不足弹窗。 */
  concurrentTaskLimitModal: ConcurrentTaskLimitModalState | null;
}

const flowNextState: StateCreator<IFlowNextState, [
  ['zustand/immer', never],
  ['zustand/subscribeWithSelector', never],
]> = () => ({
  inShared: false,
  projectId: '',
  roomId: '',
  caseId: '',
  hasNodeOperation: false,
  modelConfig: {} as ModelConfig,
  commonModelMcpInfoMap: {},
  cancelModelNodeId: '',
  canceledPollingNodeId: '',
  timbreConfigNodeId: null,
  timbreList: [],
  movingShotItems: [],
  movingShotLoaded: false,
  movingShotLoading: false,
  costNodeMap: {},
  onPreviewMedia: undefined,
  previewHeaderMenuSlots: null,
  onAddChat: null,
  isMenuFullScreen: false,
  menuFullScreenSize: MENU_FULL_SCREEN_DEFAULT_SIZE,
  queueMode: getTaskSpeed(),
  concurrentTaskLimitModal: null,
});

const useTeamFlowStore: UseBoundStore<Write<WithImmer<StoreApi<IFlowNextState>>, StoreSubscribeWithSelector<IFlowNextState>>> = create<IFlowNextState>()(immer(subscribeWithSelector(flowNextState)));
export default useTeamFlowStore;

/**
 * 设置是否为分享模式
 *
 * @export
 * @param {boolean} value - 是否为分享模式
 */
export function setInShared(value: boolean) {
  useTeamFlowStore.setState(() => ({ inShared: value }));
}

/**
 * 设置房间id
 * @export
 * @param {string} roomId
 */
export function setRoomId(roomId: string) {
  useTeamFlowStore.setState(() => ({ roomId }));
}

/** 设置当前 Team Flow 所属项目，用作本地视口状态的命名空间。 */
export function setProjectId(projectId: string) {
  useTeamFlowStore.setState(() => ({ projectId }));
}

/**
 * 设置案例id
 *
 * @export
 * @param {string} caseId
 */
export function setCaseId(caseId: string) {
  useTeamFlowStore.setState(() => ({ caseId }));
}

/**
 * 设置模型配置
 * @export
 * @param {ModelConfig} modelConfig
 */
export function setModelConfig(modelConfig: ModelConfig) {
  useTeamFlowStore.setState(() => ({ modelConfig }));
}

/**
 * 设置取消模型执行任务的节点id
 *
 * @export
 * @param {string} id
 */
export function setCancelModelNodeId(id: string) {
  useTeamFlowStore.setState(() => ({ cancelModelNodeId: id }));
}

/**
 * 关闭取消弹窗，只有当传入的id与当前store中cancelModelNodeId一致时才会关闭，避免误操作关闭其他节点的取消弹窗
 *
 * @export
 * @param {string} id
 */
export function closeCancelModelNodeId(id: string) {
  const currentId = useTeamFlowStore.getState().cancelModelNodeId;
  if (currentId === id) {
    useTeamFlowStore.setState(() => ({ cancelModelNodeId: '' }));
  }
}

/**
 * 已经取消的节点id，在轮询获取任务结果时，如果有此id，则认为任务已经取消，抛弃任务结果
 *
 * @export
 * @param {string} id
 */
export function setCanceledPollingNodeId(id: string) {
  useTeamFlowStore.setState(() => ({ canceledPollingNodeId: id }));
}

/**
 * 设置音色配置选项
 *
 * @export
 * @param {TimbreConfig} data
 */
export function setTimbreConfig(id: string | null) {
  useTeamFlowStore.setState(() => ({ timbreConfigNodeId: id }));
}

/**
 * 设置音色素材列表
 *
 * @export
 * @param {TimbreItem[]} timbreList
 */
export function setTimbreList(timbreList: TimbreItem[]) {
  useTeamFlowStore.setState(() => ({ timbreList }));
}

/**
 * 设置用户是否操作过节点
 *
 * @export
 * @param {boolean} value
 */
export function setHasNodeOperation(value: boolean) {
  useTeamFlowStore.setState(() => ({ hasNodeOperation: value }));
}

/**
 * 兼容 TeamFlow 旧队列速率入口，同时同步新的轻量任务速率 store。
 *
 * @export
 * @param {TeamFlowQueueMode} mode - 接口 task_speed 字段值
 */
export function setTeamFlowQueueMode(mode: TeamFlowQueueMode) {
  setTaskSpeed(mode);
  useTeamFlowStore.setState(() => ({ queueMode: mode }));
}

/**
 * 订阅 hasNodeOperation 状态，在由 false 变为 true 时触发回调。
 *
 * @export
 * @param {() => void} callback - hasNodeOperation 变为 true 时调用
 * @returns {() => void} 取消订阅函数
 */
export function subscribeHasNodeOperation(callback: () => void): () => void {
  return useTeamFlowStore.subscribe(
    (state) => state.hasNodeOperation,
    (hasNodeOperation, prevHasNodeOperation) => {
      if (hasNodeOperation && !prevHasNodeOperation) {
        callback();
      }
    },
  );
}

/**
 * 写入运镜素材缓存，供资源库弹窗与视频节点底部输入复用。
 */
export function setMovingShotCache(items: MaterialModalItem[]) {
  useTeamFlowStore.setState(() => ({
    movingShotItems: items,
    movingShotLoaded: true,
    movingShotLoading: false,
  }));
}

/**
 * 标记运镜素材正在请求，避免重复发起列表接口。
 */
export function setMovingShotLoading(loading: boolean) {
  useTeamFlowStore.setState(() => ({ movingShotLoading: loading }));
}

/**
 * 读取实际扣费萝卜数：有折扣价时取 discount_cost，否则取原价 cost。
 *
 * @export
 * @param {NodeCostInfo | null | undefined} info
 * @return {number}
 */
export function getEffectiveNodeCost(info?: NodeCostInfo | null): number {
  if (!info) return 0;
  return typeof info.discount_cost === 'number' ? info.discount_cost : info.cost;
}

/**
 * 是否存在可展示的折扣价（与原价不同时才双价展示）。
 *
 * @export
 * @param {NodeCostInfo | null | undefined} info
 * @return {boolean}
 */
export function hasDiscountNodeCost(info?: NodeCostInfo | null): boolean {
  return typeof info?.discount_cost === 'number' && info.discount_cost !== info.cost;
}

/**
 * 设置节点萝卜缓存（原价 + 可选折扣价）
 *
 * @export
 * @param {string} nodeId
 * @param {NodeCostInfo} costInfo
 */
export function setCostNodeMap(nodeId: string, costInfo: NodeCostInfo) {
  useTeamFlowStore.setState((state) => ({
    costNodeMap: { ...state.costNodeMap, [nodeId]: costInfo },
  }));
}

/**
 * 删除节点萝卜缓存
 *
 * @export
 * @param {string} nodeId
 */
export function removeCostNodeMap(nodeId: string) {
  useTeamFlowStore.setState((state) => ({ costNodeMap: { ...state.costNodeMap, [nodeId]: undefined } }));
}

/**
 * 注册 TeamFlow 媒体预览回调，供画布节点与 Agent 预览共用。
 */
export function setOnPreviewMedia(
  callback?: (params: { mediaList: PreviewMediaItem[]; index: number; }) => void,
) {
  useTeamFlowStore.setState(() => ({ onPreviewMedia: callback }));
}

/**
 * 设置 TeamFlow 媒体预览头部菜单插槽。
 */
export function setPreviewHeaderMenuSlots(slots: React.ReactElement | null) {
  useTeamFlowStore.setState(() => ({ previewHeaderMenuSlots: slots }));
}

/**
 * 设置添加到聊天回调
 *
 * @export
 * @param {(node: FlowNodeEntity) => void} callback
 */
export function setOnAddChat(callback: (node: FlowNodeData, flowNodeType: TVFlowNodeNextTypeEnum) => void) {
  useTeamFlowStore.setState(() => ({ onAddChat: callback }));
}

/**
 * 打开菜单全屏
 *
 * @export
 */
export function openMenuFullScreen() {
  useTeamFlowStore.setState(() => ({ isMenuFullScreen: true }));
}

/**
 * 关闭菜单全屏
 *
 * @export
 */
export function closeMenuFullScreen() {
  useTeamFlowStore.setState(() => ({ isMenuFullScreen: false }));
}

/**
 * 更新提示词全屏面板尺寸，供右下角拖拽缩放复用。
 *
 * @export
 * @param {MenuFullScreenSize} size
 */
export function setMenuFullScreenSize(size: MenuFullScreenSize) {
  useTeamFlowStore.setState(() => ({
    menuFullScreenSize: clampMenuFullScreenSize(size),
  }));
}

/**
 * 展示节点执行并发不足弹窗；重复触发时用最新接口返回数量覆盖旧弹窗内容。
 *
 * @export
 * @param {ConcurrentTaskLimitModalState} modal
 */
export function showConcurrentTaskLimitModal(modal: ConcurrentTaskLimitModalState) {
  useTeamFlowStore.setState(() => ({
    concurrentTaskLimitModal: modal,
  }));
}

/**
 * 隐藏节点执行并发不足弹窗。
 *
 * @export
 */
export function hideConcurrentTaskLimitModal() {
  useTeamFlowStore.setState(() => ({ concurrentTaskLimitModal: null }));
}

/**
 * 判断节点 mcpInfo 是否可写入公共参数缓存。
 * 撤销/新建节点过渡期可能产生未初始化的 mcpInfo，不应覆盖用户已选参数。
 *
 * @param {FlowNodeMcpInfo | undefined | null} modelMcpInfo
 * @return {boolean}
 */
function isPersistableCommonMcpInfo(modelMcpInfo: FlowNodeMcpInfo | undefined | null): modelMcpInfo is FlowNodeMcpInfo {
  if (!modelMcpInfo) {
    return false;
  }
  if (!modelMcpInfo.model_id?.trim() || !modelMcpInfo.node_model_id?.trim() || !modelMcpInfo.api_name?.trim()) {
    return false;
  }
  if (!modelMcpInfo.parameters || typeof modelMcpInfo.parameters !== 'object') {
    return false;
  }
  return true;
}

/**
 * 设置模型全局通用McpInfo
 *
 * @export
 * @param {TVFlowNodeNextTypeEnum} key
 * @param {FlowNodeMcpInfo | undefined} modelMcpInfo
 */
export function setCommonModelMcpInfoMap(key: TVFlowNodeNextTypeEnum, modelMcpInfo: FlowNodeMcpInfo | undefined) {
  if (!isPersistableCommonMcpInfo(modelMcpInfo)) {
    return;
  }
  useTeamFlowStore.setState((state) => ({
    commonModelMcpInfoMap: {
      ...state.commonModelMcpInfoMap,
      [key]: modelMcpInfo,
    },
  }));
}
